import { appConfig } from '@/config/appConfig';
import useGet from '@/hooks/useGet';
import type { ErrorResponse } from '@/types/ErrorResponse';
import type { Meta } from '@/types/Meta';
import type { PaginationResponse } from '@/types/PaginationResponse';
import type { UseGetResult } from '@/types/UseGetResult';

import { industriesRoutes } from '../routes/industriesRoutes';
import type { Industry } from '../types/Industry';

type UseFetchIndustriesParams = {
    page?: number;
    perPage?: number;
    sortBy?: string;
    orderBy?: string;
    search?: string;
};

type UseFetchIndustriesReturn = UseGetResult<PaginationResponse<Industry>, ErrorResponse> & {
    industries: Industry[];
    meta: Meta | null;
};

export default function useFetchIndustries({
    page,
    perPage,
    sortBy,
    orderBy,
    search,
}: UseFetchIndustriesParams = {}): UseFetchIndustriesReturn {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // -----------------------------------------------------------------------------------------------
    const pageNumber = page ?? appConfig.filter.defaultPage;
    const perPageNumber = perPage ?? appConfig.filter.defaultPerPage;
    const sortByQuery = sortBy ?? appConfig.filter.defaultSortBy;
    const orderByQuery = orderBy ?? appConfig.filter.defaultOrderBy;
    const searchQuery = search ?? appConfig.filter.defaultSearch;

    const queryParams = {
        [appConfig.filter.defaultPageQueryName]: pageNumber,
        [appConfig.filter.defaultPerPageQueryName]: perPageNumber,
        [appConfig.filter.defaultSortByQueryName]: sortByQuery,
        [appConfig.filter.defaultOrderByQueryName]: orderByQuery,
        [appConfig.filter.defaultSearchQueryName]: searchQuery,
    };

    const result = useGet<PaginationResponse<Industry>, ErrorResponse>(industriesRoutes.api.base(), {
        queryParams,
    });

    // ----------------------------------------------------------------------------------------------------
    // MARK: Return
    // ----------------------------------------------------------------------------------------------------
    return {
        industries: result.response?.data ?? [],
        meta: result.response?.meta ?? null,
        ...result,
    };
}
